import React, { useEffect, useState } from 'react';
import * as axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarAlt,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import htmlSerializer from '../utils/htmlSerializer';
import { RichText } from 'prismic-reactjs';
import { Bars } from 'svg-loaders-react';
import { getStandardizedDateTimeFromGCalDateTime } from '../utils/gCalDateUtils';
import { LocationMarker } from '../svgs/locationMarker';
import { Clock } from '../svgs/clock';

const RbrsGCal = (slice) => {
  if (!slice.slice) {
    return null;
  }

  const { title, description } = slice.slice.primary;

  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [calData, setCalData] = useState(null);
  const [isLoadingCalendarData, setIsLoadingCalendarData] = useState(false);
  const [calendarError, setCalendarError] = useState(null);

  const getCalendarData = async (month, year) => {
    const paddedMonth = String(month).padStart(2, '0');
    const timeMin = `${year}-${paddedMonth}-01T00%3A00%3A00Z`;
    const timeMax = `${year}-${paddedMonth}-${getMaxDay(month)}T23%3A59%3A59Z`;

    try {
      setIsLoadingCalendarData(true);
      setCalendarError(null);
      setCalData(null);

      const response = await axios.get(
        `/.netlify/functions/calendar-fetch?googleCalendarName=${process.env.GATSBY_GOOGLE_CALENDAR_NAME}&timeMin=${timeMin}&timeMax=${timeMax}`
      );
      setCalData(response.data.items);

      setIsLoadingCalendarData(false);
    } catch {
      setIsLoadingCalendarData(false);
      setCalendarError({ month, year });
    }
  };

  useEffect(() => {
    getCalendarData(currentMonth, currentYear);
  }, []);

  const isLeapYear = (year) => {
    return new Date(year, 1, 29).getDate() === 29;
  };

  const getMaxDay = (month) => {
    switch (+month) {
      case 2: {
        return isLeapYear(currentYear) ? 29 : 28;
      }
      case 1:
      case 3:
      case 5:
      case 7:
      case 8:
      case 10:
      case 12: {
        return 31;
      }
      case 4:
      case 6:
      case 9:
      case 11: {
        return 30;
      }
    }
  };

  const dayOptions = { day: 'numeric' };
  const weekdayOptions = { weekday: 'short' };
  const dateOptions = { weekday: 'long', month: 'long', day: 'numeric' };
  const timeOptions = { timeStyle: 'short', timeZone: 'America/New_York' };

  const months = [
    { name: 'January', value: 1 },
    { name: 'February', value: 2 },
    { name: 'March', value: 3 },
    { name: 'April', value: 4 },
    { name: 'May', value: 5 },
    { name: 'June', value: 6 },
    { name: 'July', value: 7 },
    { name: 'August', value: 8 },
    { name: 'September', value: 9 },
    { name: 'October', value: 10 },
    { name: 'November', value: 11 },
    { name: 'December', value: 12 },
  ];

  const range = (start, end, length = end - start + 1) =>
    Array.from({ length }, (_, i) => start + i);

  const yearBoundary = new Date().getFullYear();
  const years = range(2020, yearBoundary + 1);

  const handleSetCurrentMonth = (month) => {
    const currentMonthAsNumber = +currentMonth;
    if (currentMonthAsNumber < 12 && currentMonthAsNumber > 1) {
      setCurrentMonth(month);
      getCalendarData(month, currentYear);
    } else if (currentMonthAsNumber === 12 && month > currentMonthAsNumber) {
      setCurrentMonth(1);
      setCurrentYear(currentYear + 1);
      getCalendarData(1, currentYear + 1);
    } else if (
      (currentMonthAsNumber === 12 && month < currentMonthAsNumber) ||
      month === 12
    ) {
      setCurrentMonth(month);
      getCalendarData(month, currentYear);
    } else if (currentMonthAsNumber === 1 && month > currentMonthAsNumber) {
      setCurrentMonth(month);
      getCalendarData(month, currentYear);
    } else if (
      (currentMonthAsNumber === 1 && month < currentMonthAsNumber) ||
      month === 0
    ) {
      setCurrentMonth(12);
      setCurrentYear(currentYear - 1);
      getCalendarData(12, currentYear - 1);
    }
  };

  const handleSetCurrentYear = (year) => {
    setCurrentYear(year);
    getCalendarData(currentMonth, year);
  };

  const currentMonthName = months.find((m) => m.value === currentMonth)?.name;

  const descriptionComponent = (
    <div className='mb-8 text-lg leading-relaxed text-gray-600 md:text-justify'>
      <RichText render={description.raw} htmlSerializer={htmlSerializer} />
    </div>
  );

  const today = new Date();
  const isCurrentMonth =
    currentMonth === today.getMonth() + 1 &&
    currentYear === today.getFullYear();

  const handleGoToToday = () => {
    const m = today.getMonth() + 1;
    const y = today.getFullYear();
    setCurrentMonth(m);
    setCurrentYear(y);
    getCalendarData(m, y);
  };

  let content = null;

  if (isLoadingCalendarData) {
    content = (
      <div className='flex w-full flex-col items-center py-20'>
        <Bars stroke='#002868' fill='#002868' className='w-10' />
        <p className='mt-4 text-sm tracking-widest text-gray-400 uppercase'>
          Loading events
        </p>
      </div>
    );
  } else if (calendarError) {
    content = (
      <div className='flex w-full flex-col items-center py-20'>
        <p className='text-lg font-medium text-gray-500'>
          Unable to load events
        </p>
        <button
          type='button'
          className='mt-4 rounded-full border border-primary px-5 py-2 text-sm font-semibold text-primary transition-colors duration-200 hover:bg-primary hover:text-white'
          onClick={() =>
            getCalendarData(calendarError.month, calendarError.year)
          }
        >
          Try again
        </button>
      </div>
    );
  } else if (calData && calData.length === 0) {
    content = (
      <div className='flex w-full flex-col items-center py-20'>
        <div
          className='mb-4 flex h-16 w-16 items-center justify-center rounded-full'
          style={{ backgroundColor: 'rgba(0, 40, 104, 0.06)' }}
        >
          <FontAwesomeIcon
            icon={faCalendarAlt}
            className='text-xl text-primary opacity-40'
            aria-hidden
          />
        </div>
        <p className='text-lg font-medium text-gray-400'>
          No events this month
        </p>
      </div>
    );
  } else if (calData && calData.length > 0) {
    content = calData.map((item, index) => {
      const startDate = getStandardizedDateTimeFromGCalDateTime(item.start);
      const dayNum = startDate.toLocaleDateString(undefined, dayOptions);
      const weekday = startDate
        .toLocaleDateString(undefined, weekdayOptions)
        .toUpperCase();

      return (
        <div
          key={index}
          className='group flex gap-4 py-5 md:gap-6'
          style={{
            borderBottom: '1px solid rgba(0, 40, 104, 0.08)',
          }}
        >
          {/* Date column */}
          <div className='flex w-10 flex-shrink-0 flex-col items-center md:w-14'>
            <span
              className='text-xxs font-semibold tracking-widest text-gray-400 md:text-xs'
              style={{ letterSpacing: '0.15em' }}
            >
              {weekday}
            </span>
            <span className='text-2xl font-semibold leading-none text-primary md:text-4xl'>
              {dayNum}
            </span>
          </div>

          {/* Vertical accent line */}
          <div
            className='w-px flex-shrink-0 self-stretch'
            style={{
              background:
                'linear-gradient(to bottom, #002868 0%, rgba(0,40,104,0.1) 100%)',
            }}
          />

          {/* Event details */}
          <div className='flex min-w-0 flex-1 flex-col justify-center'>
            <div className='flex items-start justify-between gap-2'>
              <h3 className='text-base font-semibold leading-snug text-gray-900 md:text-xl'>
                {item.summary}
              </h3>
              <a
                href={item.htmlLink}
                rel='noopener noreferrer'
                target='_blank'
                className='mt-1 flex-shrink-0 rounded-full p-1.5 text-primary opacity-30 transition-opacity duration-200 hover:opacity-100'
                style={{ backgroundColor: 'rgba(0, 40, 104, 0.04)' }}
                aria-label='Add to Google Calendar'
              >
                <FontAwesomeIcon
                  icon={faCalendarAlt}
                  className='text-sm'
                  aria-hidden
                />
              </a>
            </div>

            {item.summary === 'Off' ? null : (
              <div className='mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1'>
                <span className='inline-flex items-center gap-1.5 text-xs text-gray-500 md:text-sm'>
                  <Clock />
                  <span>
                    {startDate.toLocaleTimeString(undefined, timeOptions)}
                    {item.end.dateTime ? (
                      <>
                        {' \u2013 '}
                        {getStandardizedDateTimeFromGCalDateTime(
                          item.end
                        ).toLocaleTimeString(undefined, timeOptions)}
                      </>
                    ) : null}
                  </span>
                </span>
                {item.location ? (
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${item.location}`}
                    rel='noopener noreferrer'
                    target='_blank'
                    className='inline-flex items-center gap-1 text-xs text-gray-500 transition-colors duration-200 hover:text-primary md:text-sm'
                  >
                    <LocationMarker />
                    <span className='break-words'>{item.location}</span>
                  </a>
                ) : null}
              </div>
            )}
          </div>
        </div>
      );
    });
  }

  return (
    <>
      <div className='my-12 w-full px-4 md:w-2/3 md:px-0 mega:w-2/5'>
        {/* Section header */}
        <div className='mb-10 flex flex-col items-center text-center'>
          <p
            className='text-xs font-semibold tracking-widest text-primary uppercase'
            style={{ letterSpacing: '0.2em' }}
          >
            Schedule
          </p>
          <p className='mt-2 font-display text-3xl text-gray-900 md:text-4xl'>
            {RichText.asText(title.raw)}
          </p>
          <div
            className='mt-4 h-px w-12'
            style={{ backgroundColor: '#002868' }}
          />
          {description.raw[0]?.text ? (
            <div className='mt-6 max-w-lg'>{descriptionComponent}</div>
          ) : null}
        </div>

        {/* Month/year navigation */}
        <div
          className='mb-8 flex items-center justify-between rounded-lg px-4 py-3'
          style={{ backgroundColor: 'rgba(0, 40, 104, 0.03)' }}
        >
          <button
            type='button'
            className='flex h-9 w-9 items-center justify-center rounded-full text-primary transition-colors duration-200 hover:bg-white hover:shadow-sm'
            onClick={() => handleSetCurrentMonth(+currentMonth - 1)}
            aria-label='Previous month'
          >
            <FontAwesomeIcon icon={faChevronLeft} className='text-sm' aria-hidden />
          </button>

          <div className='flex items-center gap-2'>
            <label htmlFor='rbrs-calendar-month' className='sr-only'>
              Select month
            </label>
            <select
              id='rbrs-calendar-month'
              className='cursor-pointer appearance-none border-0 bg-transparent pr-1 text-center text-lg font-semibold text-gray-900 focus:outline-none'
              style={{ fontFamily: "'Inter var', sans-serif" }}
              onChange={(e) => handleSetCurrentMonth(+e.target.value)}
              value={currentMonth}
              aria-label='Select month'
            >
              {months.map((month, index) => (
                <option key={index} value={month.value} style={{ fontFamily: "'Inter var', sans-serif", fontWeight: 400 }}>
                  {month.name}
                </option>
              ))}
            </select>
            <label htmlFor='rbrs-calendar-year' className='sr-only'>
              Select year
            </label>
            <select
              id='rbrs-calendar-year'
              className='cursor-pointer appearance-none border-0 bg-transparent text-center text-lg font-semibold text-gray-900 focus:outline-none'
              style={{ fontFamily: "'Inter var', sans-serif" }}
              onChange={(e) => handleSetCurrentYear(+e.target.value)}
              value={currentYear}
              aria-label='Select year'
            >
              {years.map((year, index) => (
                <option key={index} value={year} style={{ fontFamily: "'Inter var', sans-serif", fontWeight: 400 }}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          <button
            type='button'
            className='flex h-9 w-9 items-center justify-center rounded-full text-primary transition-colors duration-200 hover:bg-white hover:shadow-sm'
            onClick={() => handleSetCurrentMonth(+currentMonth + 1)}
            aria-label='Next month'
          >
            <FontAwesomeIcon icon={faChevronRight} className='text-sm' aria-hidden />
          </button>
        </div>
        {!isCurrentMonth && (
          <div className='mb-8 flex justify-center -mt-4'>
            <button
              type='button'
              className='text-sm font-medium text-primary opacity-60 transition-opacity duration-200 hover:opacity-100'
              onClick={handleGoToToday}
            >
              Back to this month
            </button>
          </div>
        )}
      </div>

      {/* Events list */}
      <div className='w-full px-4 md:w-2/3 md:px-0 mega:w-2/5' style={{ minHeight: '60vh' }}>
        {content}
      </div>
    </>
  );
};

export default RbrsGCal;
